'use strict';

const libWorkspace = require('pict-section-workspace');
const libEntityPickerSearch = require('../services/Entity-Picker-Search.js');

/**
 * AssetWorkspaceProvider — the engine behind the unified Asset view (#/Asset/Workspace/:ID[/:Tab]), a subclass
 * of the pict-section-workspace base. The base owns the shell (header, tab bar, routing, load); this subclass
 * keeps Asset's bespoke tab bodies (all `render` kind): Overview · Location (interactive Leaflet map, reusing
 * ProjectCenterMap) · Elements · Defects · Observations · Documents · Migrate (single-asset legacy→modern
 * element migration). Config in [[Asset.config.js]].
 *
 * ELEMENT MANAGEMENT (AASHTO element-level inspection). The Elements tab is a master→detail editor for a
 * super-user / customer-admin:
 *   • master — the asset's PhysicalAssetElement instances (add from the element-type catalog / soft-remove),
 *     each row showing its latest condition's CS1–4 distribution.
 *   • detail — one element's full record: inline Quantity edit; the per-year CONDITION history
 *     (PhysicalAssetElementCondition, CS1–4 + total per Year, add/edit/remove); and the DEFECTS recorded
 *     against the element (PhysicalAssetElementPhysicalAssetDefectJoin), each with its own per-year CS1–4
 *     breakdown (add from the element-type's defect catalog / edit / soft-remove).
 * The condition breakdown lives in PhysicalAssetElementCondition.QuantitiesJSON; the per-defect breakdown lives
 * in the element↔defect join's QuantitiesJSON/TotalQuantity/Year (columns added to that join — a bare link
 * before — so a defect carries the AASHTO condition-state quantities it accounts for). See the schema change in
 * web/contract_admin/schema/MeadowSchema-PhysicalAssetElementPhysicalAssetDefectJoin.json.
 *
 * The map reuses the app's ProjectCenterMap via a third instance (AssetLocationMap, ProviderName='AssetWorkspace',
 * MapHostID='AW-MapHost'); this provider therefore implements the map contract (center / hasCenter / setCenter)
 * exactly like ProjectWorkspace, and setCenter does a LIGHT readout update (never a full body re-render) so a
 * marker drag never tears the live map down. The map is (re)mounted from the Location tab's OnEnter hook.
 */
class AssetWorkspaceProvider extends libWorkspace.HeadlightWorkspaceBaseProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._asset = null;
		this._typeName = '';
		this._classificationName = '';
		this._center = { Latitude: null, Longitude: null };
		this._centerDirty = false;
		this._locError = '';
		this._tabs = {};      // per-tab loaded data cache
		this._migrate = { Plan: null, Applying: false, Result: null, Confirming: false, Error: '' };
		this._elementDetail = null;   // the open element (master→detail) or null
		this._condEdit = null;        // { ID } — the condition being added (ID 0) / edited, or null
		this._defEdit = null;         // { ID, IDDefect, Label } — the defect-on-element being added (ID 0) / edited, or null
		this._confirm = null;         // { kind:'element'|'condition'|'defect', id } — inline delete confirm
		this._elemError = '';         // last element/condition/defect write error (shown in the Elements tab)
		this._customProps = null;     // { Loaded, HasShape, Accessor, Values:{setKey:{addr:val}}, RowIDs, Error, FromFallback } — Overview custom-property render
		this._cpEdit = null;          // { On, Draft:{code:val}, Saving, Error } — per-asset custom-property VALUE edit mode
	}

	get currentUser() { return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {}; }
	get customerName() { return this.currentUser.CustomerName || ('Customer ' + this.currentUser.CustomerID); }
	get idCustomer() { return Number(this.currentUser.CustomerID) || (this._asset && this._asset.IDCustomer) || 0; }
	get isSuperUser() { const u = this.currentUser; return (Number(u.UserRoleIndex) === 5) && (Number(u.CustomerID) === 1); }
	get hasCustomerAdmin() { const r = this.currentUser.UserRoles; return Array.isArray(r) && r.some((x) => x && (x.PermissionLevel === 'CustomerAdmin' || x.PermissionLevel === 'SuperAdminUser')); }
	get canEdit() { return this.isSuperUser || this.hasCustomerAdmin; }
	get idAsset() { return this._asset ? this._asset.IDPhysicalAsset : null; }
	get providerName() { return this.identifier || 'AssetWorkspace'; }

	// Map contract (consumed by the AssetLocationMap ProjectCenterMap instance).
	get center() { return this._center; }
	get hasCenter() { return (this._center.Latitude != null) && (this._center.Longitude != null) && !((Number(this._center.Latitude) === 0) && (Number(this._center.Longitude) === 0)); }

	// --- REST helpers (reject-on-error variants the Asset loaders were written against) ---
	_get(pURI) { return new Promise((res, rej) => this.restClient.getJSON(pURI, (e, b) => e ? rej(e) : res(b))); }
	_post(pURI, pBody) { return new Promise((res, rej) => this.restClient.postJSON(pURI, pBody, (e, b) => e ? rej(e) : res(b))); }
	_put(pURI, pBody) { return new Promise((res, rej) => this.restClient.putJSON(pURI, pBody, (e, b) => e ? rej(e) : res(b))); }
	// DELETE a single entity row (soft-delete where the schema has a Deleted column). Mirrors the Inspection
	// workspace: prefer the rest client's deleteEntity, fall back to a raw DELETE.
	_del(pURI)
	{
		return new Promise((res, rej) =>
		{
			const m = /^([A-Za-z][A-Za-z0-9]*)\/(\d+)$/.exec(String(pURI));
			if (m && this.restClient && (typeof this.restClient.deleteEntity === 'function')) { this.restClient.deleteEntity(m[1], m[2], (e, b) => e ? rej(e) : res(b)); return; }
			const tmpBase = (this.restClient && this.restClient.serverURL) || '/1.0/';
			fetch(`${tmpBase}${pURI}`, { method: 'DELETE', credentials: 'include' })
				.then((r) => { if (!r.ok) { rej(new Error(`HTTP ${r.status}`)); return; } r.text().then((t) => res(t ? JSON.parse(t) : {})).catch(() => res({})); })
				.catch(rej);
		});
	}
	async _getAllPages(pURI, pPage = 250)
	{
		const tmpOut = []; let o = 0;
		for (;;) { const rows = await this._get(`${pURI}/${o}/${pPage}`); if (!Array.isArray(rows) || rows.length === 0) { break; } tmpOut.push(...rows); if (rows.length < pPage) { break; } o += pPage; if (o > 200000) { break; } }
		return tmpOut;
	}
	static _index(pRows, pKey) { const m = {}; (pRows || []).forEach((r) => { if (r && r[pKey] != null) { m[String(r[pKey])] = r; } }); return m; }
	_n(v) { const n = Number(v); return Number.isNaN(n) ? '—' : n.toLocaleString('en-US'); }
	_num0(v) { const n = Number(v); return Number.isNaN(n) ? 0 : n; }
	_val(pSel) { const CA = this.pict.ContentAssignment; const el = CA && CA.getElement(pSel); return (el && el.length) ? el[0].value : ''; }
	// Escape for a single-quoted JS string arg inside a double-quoted HTML attribute (field codes are authored).
	_js(pStr) { return String(pStr == null ? '' : pStr).replace(/&/g, '&amp;').replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/"/g, '&quot;'); }
	// Meadow serves a `<col>JSON` column read as the parsed object under the un-suffixed name (Quantities),
	// while accepting writes under either name. Read Quantities; fall back to parsing the raw string.
	_normalizeCS(pRow)
	{
		if (!pRow) { return null; }
		if (pRow.Quantities && typeof pRow.Quantities === 'object') { return pRow.Quantities; }
		const raw = pRow.Quantities || pRow.QuantitiesJSON; if (!raw) { return null; }
		try { return JSON.parse(raw); } catch (e) { return null; }
	}

	// --- hydrate (runs inside the base load, after the PhysicalAsset record arrives) ---
	async hydrateAsync(pRecord)
	{
		this._asset = pRecord;
		this._tabs = {};
		this._migrate = { Plan: null, Applying: false, Result: null, Confirming: false, Error: '' };
		this._elementDetail = null; this._condEdit = null; this._defEdit = null; this._confirm = null; this._elemError = '';
		this._center = { Latitude: pRecord.OriginLatitude != null ? Number(pRecord.OriginLatitude) : null, Longitude: pRecord.OriginLongitude != null ? Number(pRecord.OriginLongitude) : null };
		this._centerDirty = false;
		if (this.pict && this.pict.AppData) { this.pict.AppData.AssetWorkspace = this.pict.AppData.AssetWorkspace || {}; }
		// FK name lookups (may be owned by a shared/platform customer → not readable → fall back to the id).
		this._typeName = await this._lookupName('AssetType', pRecord.IDAssetType, 'Name');
		this._classificationName = await this._lookupName('AssetClassification', pRecord.IDAssetClassification, 'Name');
		// Stash the resolved names on the record so the base header Subtitle(rec) can read them.
		pRecord.__typeName = this._typeName;
		pRecord.__classificationName = this._classificationName;
		// Resolve the type's published metadata shape + this asset's stored values for the Overview panel.
		await this._loadCustomPropsAsync();
	}

	// Load this asset type's published descriptor (if any) + this asset's PhysicalAssetMetadata values, for a
	// read-only custom-property panel on the Overview. Non-fatal: a failure leaves the rest of the workspace intact.
	async _loadCustomPropsAsync()
	{
		this._cpEdit = null;
		this._customProps = { Loaded: false, HasShape: false, Accessor: null, Values: {}, RowIDs: {}, Error: '', FromFallback: false };
		try
		{
			const tmpProv = this.pict.providers && this.pict.providers.AssetDescriptor;
			if (!tmpProv || (typeof tmpProv.accessorForTypeAsync !== 'function')) { this._customProps.Loaded = true; return; }
			const tmpIDType = this._asset.IDAssetType;
			const tmpVersion = this._asset.ManifestVersion;
			const tmpRows = await this._getAllPages(`PhysicalAssetMetadatas/FilteredTo/FBV~IDPhysicalAsset~EQ~${this.idAsset}~FBV~Deleted~EQ~0`);
			const tmpBySet = {}; const tmpRowIDs = {};
			tmpRows.forEach((r) => { const s = r.MetadataSetGUID || ''; const a = r.Address; if (a == null) { return; } (tmpBySet[s] = tmpBySet[s] || {})[a] = (r.Value != null && r.Value !== '') ? r.Value : r.MetadataValue; (tmpRowIDs[s] = tmpRowIDs[s] || {})[a] = r.IDPhysicalAssetMetadata; });
			const tmpDescriptor = await tmpProv.descriptorForTypeAsync(tmpIDType, tmpVersion);
			// Render when the type has an authored (published) shape, OR when the asset simply has stored values —
			// bridges carry SNBI data the compiled-in fallback vocabulary decodes. Otherwise there is nothing to show.
			if (!tmpDescriptor && !tmpRows.length) { this._customProps.Loaded = true; return; }
			const tmpAccessor = await tmpProv.accessorForTypeAsync(tmpIDType, { ManifestVersion: tmpVersion });
			this._customProps = { Loaded: true, HasShape: true, Accessor: tmpAccessor, Values: tmpBySet, RowIDs: tmpRowIDs, Error: '', FromFallback: !tmpDescriptor };
		}
		catch (e) { this._customProps = { Loaded: true, HasShape: false, Accessor: null, Values: {}, Error: (e && e.message) || String(e), FromFallback: false }; }
	}

	// The Overview's custom-property panel: the type's visible fields (primary set) with this asset's values,
	// read-only (editing the SHAPE is on the AssetType workspace; per-asset value editing is a later piece).
	_customPropsHTML()
	{
		const cp = this._customProps;
		if (!cp || !cp.Loaded) { return `<div class="aw-cp-note"><i class="fas fa-circle-notch fa-spin"></i> Loading custom properties…</div>`; }
		if (cp.Error) { return `<div class="aw-cp-note">Custom properties could not be loaded — ${this._esc(cp.Error)}</div>`; }
		const tmpIDType = this._asset && this._asset.IDAssetType;
		if (!cp.HasShape || !cp.Accessor)
		{
			const tmpLink = tmpIDType ? ` <a class="aw-link" href="#/AssetType/Workspace/${tmpIDType}/metadata">Define it</a>` : '';
			return `<div class="aw-cp-note"><i class="fas fa-circle-info"></i> No custom-property shape published for this asset type, and no values recorded for this asset yet.${tmpLink}</div>`;
		}
		const acc = cp.Accessor;
		const tmpVals = (cp.Values && cp.Values[acc.setKey]) || {};
		const tmpGroups = acc.visibleGroups();
		// With an authored shape, show every declared visible field (blank → "—"). With only the fallback
		// vocabulary (e.g. a bridge's SNBI data, no custom shape), show only the fields the asset has values for,
		// so the panel is the asset's data — not a 150-row dump of the whole standard dictionary.
		const tmpEditing = !!(this._cpEdit && this._cpEdit.On);
		// Value editing is offered only for an AUTHORED shape (the SNBI fallback is inspection/ingested data).
		const tmpCanEditValues = this.canEdit && !cp.FromFallback;
		const tmpOnlyPopulated = !!cp.FromFallback && !tmpEditing;
		const tmpBlocks = tmpGroups.map((g) =>
		{
			let tmpFields = g.Fields;
			if (tmpOnlyPopulated) { tmpFields = tmpFields.filter((f) => { const raw = tmpVals[f.Code]; return (raw != null && raw !== ''); }); }
			if (!tmpFields.length) { return ''; }
			const tmpRows = tmpFields.map((f) =>
			{
				if (tmpEditing)
				{
					return `<div class="aw-field"><div class="aw-field-label">${this._esc(f.Label)}${f.Units ? ` <span class="aw-muted">(${this._esc(f.Units)})</span>` : ''}${(f.Constraints && f.Constraints.Required) ? ' <span class="aw-req" title="Required">*</span>' : ''}</div><div class="aw-field-value">${this._cpFieldEditHTML(f)}</div></div>`;
				}
				const raw = tmpVals[f.Code];
				const shown = (raw == null || raw === '') ? '<span class="aw-muted">—</span>' : this._esc(acc.readable(f.Code, raw));
				return `<div class="aw-field"><div class="aw-field-label">${this._esc(f.Label)}${f.Units ? ` <span class="aw-muted">(${this._esc(f.Units)})</span>` : ''}</div><div class="aw-field-value">${shown}</div></div>`;
			}).join('');
			return `<div class="aw-cp-group"><div class="aw-cp-group-h">${this._esc(g.Label)}</div><div class="aw-fields">${tmpRows}</div></div>`;
		}).filter(Boolean).join('');
		if (!tmpBlocks && !tmpEditing) { return `<div class="aw-cp-note"><i class="fas fa-circle-info"></i> No custom-property values recorded for this asset yet.${tmpCanEditValues ? ' <a class="aw-link" onclick="_Pict.providers.AssetWorkspace.startCPEdit()">Add values</a>' : ''}${tmpIDType ? ` · <a class="aw-link" href="#/AssetType/Workspace/${tmpIDType}/metadata">Define its shape</a>` : ''}</div>`; }
		const tmpErr = (tmpEditing && this._cpEdit.Error) ? `<div class="aw-error" style="margin:0.5rem 0 0">${this._esc(this._cpEdit.Error)}</div>` : '';
		const tmpActions = tmpEditing
			? `<span class="aw-cp-actions"><button class="aw-btn aw-btn-primary aw-btn-sm" ${this._cpEdit.Saving ? 'disabled' : ''} onclick="_Pict.providers.AssetWorkspace.saveCPValuesAsync()">${this._cpEdit.Saving ? 'Saving…' : 'Save values'}</button><button class="aw-btn aw-btn-ghost aw-btn-sm" onclick="_Pict.providers.AssetWorkspace.cancelCPEdit()">Cancel</button></span>`
			: `<span class="aw-cp-actions">${tmpCanEditValues ? `<button class="aw-btn aw-btn-ghost aw-btn-sm" onclick="_Pict.providers.AssetWorkspace.startCPEdit()"><i class="fas fa-pen"></i> Edit values</button>` : ''}${tmpIDType ? ` <a class="aw-link aw-cp-edit" href="#/AssetType/Workspace/${tmpIDType}/metadata">Edit shape</a>` : ''}</span>`;
		return `<div class="aw-cp-head">Custom properties<span class="aw-muted"> · ${this._esc(acc.setLabel)}</span>${tmpActions}</div>${tmpErr}${tmpBlocks}`;
	}

	// --- per-asset custom-property VALUE editing (authored shapes only; upserts PhysicalAssetMetadata) ---
	_repaintOverview() { this.repaintBody(); this._scheduleCPMount(); }
	_scheduleCPMount() { try { setTimeout(() => this._mountCPPickers(), 0); } catch (e) { /* */ } }

	/** The input widget for one field in edit mode. Enums mount a picker (house rule); others are typed inputs. */
	_cpFieldEditHTML(pField)
	{
		const tmpCode = this._js(pField.Code);
		const tmpDraft = (this._cpEdit && this._cpEdit.Draft) || {};
		const tmpVal = (tmpDraft[pField.Code] != null) ? tmpDraft[pField.Code] : '';
		const dt = pField.DataType;
		if (dt === 'enum') { return `<div id="AW-CP-${this._esc(pField.Code)}" class="aw-cp-pick"></div>`; }
		if (dt === 'boolean')
		{
			const on = (tmpVal === true || String(tmpVal) === 'true' || String(tmpVal) === '1');
			return `<label class="aw-cp-chk"><input type="checkbox" ${on ? 'checked' : ''} onchange="_Pict.providers.AssetWorkspace.setCPValue('${tmpCode}', this.checked ? 'true' : 'false')"></label>`;
		}
		if (dt === 'text') { return `<textarea class="aw-in aw-cp-in aw-cp-ta" oninput="_Pict.providers.AssetWorkspace.setCPValue('${tmpCode}', this.value)">${this._esc(tmpVal)}</textarea>`; }
		const tmpInputType = (dt === 'number' || dt === 'integer') ? 'number' : (dt === 'date' ? 'date' : 'text');
		const tmpStep = (dt === 'integer') ? ' step="1"' : (dt === 'number' ? ' step="any"' : '');
		const tmpShownVal = (dt === 'date') ? String(tmpVal).slice(0, 10) : tmpVal;
		return `<input class="aw-in aw-cp-in" type="${tmpInputType}"${tmpStep} value="${this._esc(tmpShownVal)}" oninput="_Pict.providers.AssetWorkspace.setCPValue('${tmpCode}', this.value)">`;
	}

	startCPEdit()
	{
		const cp = this._customProps;
		if (!this.canEdit || !cp || !cp.HasShape || cp.FromFallback || !cp.Accessor) { return; }
		if (this.pict && this.pict.AppData) { this.pict.AppData.AssetWorkspace = this.pict.AppData.AssetWorkspace || {}; this.pict.AppData.AssetWorkspace.CPPick = {}; }
		const tmpVals = (cp.Values && cp.Values[cp.Accessor.setKey]) || {};
		this._cpEdit = { On: true, Draft: Object.assign({}, tmpVals), Saving: false, Error: '' };
		this._repaintOverview();
	}
	cancelCPEdit() { this._cpEdit = null; this._repaintOverview(); }
	setCPValue(pCode, pValue) { if (this._cpEdit && this._cpEdit.Draft) { this._cpEdit.Draft[pCode] = pValue; } }

	async saveCPValuesAsync()
	{
		const cp = this._customProps; const ed = this._cpEdit;
		if (!ed || ed.Saving || !this.canEdit || !cp || !cp.Accessor) { return; }
		const acc = cp.Accessor; const tmpSetKey = acc.setKey;
		const tmpOrig = (cp.Values && cp.Values[tmpSetKey]) || {};
		const tmpRowIDs = (cp.RowIDs && cp.RowIDs[tmpSetKey]) || {};
		const tmpVisible = [];
		acc.visibleGroups().forEach((g) => g.Fields.forEach((f) => tmpVisible.push(f)));
		const tmpMissing = tmpVisible.filter((f) => f.Constraints && f.Constraints.Required && (ed.Draft[f.Code] == null || String(ed.Draft[f.Code]).trim() === ''));
		if (tmpMissing.length) { ed.Error = `These are required: ${tmpMissing.map((f) => f.Label || f.Code).join(', ')}.`; this._repaintOverview(); return; }
		const tmpChanges = tmpVisible.filter((f) => String(ed.Draft[f.Code] == null ? '' : ed.Draft[f.Code]) !== String(tmpOrig[f.Code] == null ? '' : tmpOrig[f.Code]));
		if (!tmpChanges.length) { this._cpEdit = null; this._repaintOverview(); return; }
		ed.Saving = true; ed.Error = ''; this._repaintOverview();
		try
		{
			for (const f of tmpChanges)
			{
				const v = (ed.Draft[f.Code] == null) ? '' : String(ed.Draft[f.Code]);
				const tmpExistingID = tmpRowIDs[f.Code];
				if (tmpExistingID) { const r = await this._put('PhysicalAssetMetadata', { IDPhysicalAssetMetadata: tmpExistingID, Value: v, MetadataValue: v }); if (r && r.Error) { throw new Error(r.Error); } }
				else if (v !== '') { const r = await this._post('PhysicalAssetMetadatas', { IDPhysicalAsset: this.idAsset, MetadataSetGUID: tmpSetKey, Address: f.Code, Value: v, MetadataValue: v, Units: f.Units || '' }); if (r && r.Error) { throw new Error(r.Error); } }
			}
			await this._loadCustomPropsAsync(); // reloads values + clears edit mode
		}
		catch (e) { ed.Saving = false; ed.Error = `Save failed — ${(e && e.message) || String(e)}`; }
		this._repaintOverview();
	}

	_mountCPPickers()
	{
		if (!this._cpEdit || !this._cpEdit.On) { return; }
		const cp = this._customProps; if (!cp || !cp.Accessor) { return; }
		const tmpPicker = this._picker(); const tmpCA = this.pict.ContentAssignment;
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function') || !tmpCA) { return; }
		const self = this;
		cp.Accessor.visibleGroups().forEach((g) => g.Fields.forEach((f) =>
		{
			if (f.DataType !== 'enum') { return; }
			if (tmpCA.getElement(`#AW-CP-${f.Code}`).length === 0) { return; }
			const v = tmpPicker.createPicker(`AssetWorkspace-CP-${f.Code}`, {
				DestinationAddress: `#AW-CP-${f.Code}`, ValueAddress: `AppData.AssetWorkspace.CPPick.${f.Code}`,
				Searchable: true, AllowClear: true, ClearLabel: '— none —', Placeholder: 'choose…',
				DataProvider: self._cpValueDataProvider(f),
				ResolveValue: () => Promise.resolve((self._cpEdit && self._cpEdit.Draft) ? self._cpEdit.Draft[f.Code] : null),
				OnChange: (val) => { self.setCPValue(f.Code, (val == null ? '' : String(val))); },
			});
			if (v && (typeof v.render === 'function')) { v.render(); }
		}));
	}
	_cpValueDataProvider(pField)
	{
		const tmpValues = (pField.Values || []).slice().sort((a, b) => (a.Ordinal - b.Ordinal)).map((v) => ({ Value: v.Value, Text: `${v.Value} · ${v.Label || v.Value}`, Record: v }));
		return (pTerm) => { const t = String(pTerm == null ? '' : pTerm).toLowerCase().trim(); const res = t ? tmpValues.filter((x) => (String(x.Text).toLowerCase().indexOf(t) > -1)) : tmpValues; return Promise.resolve({ results: res, hasMore: false }); };
	}

	async _lookupName(pEntity, pID, pField)
	{
		if (!pID) { return ''; }
		try { const r = await this._get(`${pEntity}/${Number(pID)}`); return (r && !r.Error && r[pField]) ? r[pField] : `#${pID}`; }
		catch (e) { return `#${pID}`; }
	}

	// --- Tab data (async tabs load on enter, then repaint the body) ---
	async _ensureTabDataAsync(pTab, pForce)
	{
		if (!this._asset) { return; }
		if (!pForce && this._tabs[pTab] && this._tabs[pTab].Loaded) { if (pTab === 'elements') { this._scheduleElementsMount(); } return; }
		this._tabs[pTab] = { Loading: true, Loaded: false };
		this.repaintBody();
		try
		{
			if (pTab === 'elements') { this._tabs[pTab].Rows = await this._loadElements(); }
			else if (pTab === 'defects') { this._tabs[pTab].Rows = await this._loadDefects(); }
			else if (pTab === 'observations') { const r = await this._loadObservations(); this._tabs[pTab].Rows = r.rows; this._tabs[pTab].IDProject = r.idProject; }
			else if (pTab === 'documents') { this._tabs[pTab].Rows = await this._loadDocuments(); }
			this._tabs[pTab].Loaded = true;
		}
		catch (e) { this._tabs[pTab].Error = (e && e.message) || String(e); this._tabs[pTab].Loaded = true; }
		finally { this._tabs[pTab].Loading = false; this.repaintBody(); if (pTab === 'elements') { this._scheduleElementsMount(); } }
	}

	/** Location tab OnEnter: (re)mount the reused Leaflet map into the freshly-painted #AW-MapHost. showForProject
	 *  tears down any prior map first, so this is safe on every entry; marker drags go through setCenter (a targeted
	 *  readout update, NOT a body repaint) so the live map is never torn down. */
	_mountMap()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA && tmpCA.getElement('#AW-MapHost').length > 0)
		{
			const tmpMap = this.pict.views.AssetLocationMap;
			if (tmpMap && (typeof tmpMap.showForProject === 'function')) { tmpMap.showForProject(); }
		}
	}

	async _loadElements()
	{
		const tmpElements = await this._getAllPages(`PhysicalAssetElements/FilteredTo/FBV~IDPhysicalAsset~EQ~${this.idAsset}~FBV~Deleted~EQ~0`);
		const tmpConditions = await this._getAllPages(`PhysicalAssetElementConditions/FilteredTo/FBV~IDPhysicalAsset~EQ~${this.idAsset}~FBV~Deleted~EQ~0`);
		const tmpByElement = {};
		tmpConditions.forEach((c) => { const k = String(c.IDPhysicalAssetElement); (tmpByElement[k] = tmpByElement[k] || []).push(c); });
		return tmpElements.map((e) =>
		{
			const conds = (tmpByElement[String(e.IDPhysicalAssetElement)] || [])
				.map((c) => ({ ID: c.IDPhysicalAssetElementCondition, Year: c.Year || '', Total: c.TotalQuantity, CS: this._normalizeCS(c) }))
				.sort((a, b) => String(b.Year).localeCompare(String(a.Year)));
			return {
				ID: e.IDPhysicalAssetElement,
				IDType: e.IDPhysicalAssetElementType,
				IDMaterial: e.IDPhysicalAssetMaterial,
				Name: e.Name, Units: e.Units, Quantity: e.Quantity,
				Conditions: conds,
				Latest: conds.length ? conds[0] : null,
			};
		});
	}

	async _loadDefects()
	{
		const tmpJoins = await this._getAllPages(`PhysicalAssetPhysicalAssetDefectJoins/FilteredTo/FBV~IDPhysicalAsset~EQ~${this.idAsset}`);
		const tmpIDs = [ ...new Set(tmpJoins.map((j) => j.IDPhysicalAssetDefect).filter(Boolean)) ];
		if (!tmpIDs.length) { return []; }
		const tmpDefects = await this._getAllPages(`PhysicalAssetDefects/FilteredTo/FBL~IDPhysicalAssetDefect~INN~${tmpIDs.join(',')}~FBV~Deleted~EQ~0`);
		return tmpDefects.map((d) => ({ Code: d.Code, Name: d.Name, Description: d.Description }));
	}

	// Defects recorded against ONE element (the AASHTO per-element defect list, each with its own CS breakdown).
	async _loadElementDefects(pIDElement)
	{
		const tmpJoins = await this._getAllPages(`PhysicalAssetElementPhysicalAssetDefectJoins/FilteredTo/FBV~IDPhysicalAssetElement~EQ~${pIDElement}~FBV~Deleted~EQ~0`);
		if (!tmpJoins.length) { return []; }
		const tmpIDs = [ ...new Set(tmpJoins.map((j) => j.IDPhysicalAssetDefect).filter(Boolean)) ];
		const tmpDefects = tmpIDs.length ? await this._getAllPages(`PhysicalAssetDefects/FilteredTo/FBL~IDPhysicalAssetDefect~INN~${tmpIDs.join(',')}~FBV~Deleted~EQ~0`) : [];
		const tmpByID = AssetWorkspaceProvider._index(tmpDefects, 'IDPhysicalAssetDefect');
		return tmpJoins.map((j) =>
		{
			const d = tmpByID[String(j.IDPhysicalAssetDefect)] || {};
			return { ID: j.IDPhysicalAssetElementPhysicalAssetDefectJoin, IDDefect: j.IDPhysicalAssetDefect, Code: d.Code || '', Name: d.Name || `#${j.IDPhysicalAssetDefect}`, Year: j.Year || '', Total: j.TotalQuantity, CS: this._normalizeCS(j) };
		}).sort((a, b) => String(a.Code).localeCompare(String(b.Code)));
	}

	// The defects the element's TYPE allows (AASHTO catalog) — used to scope the add-defect picker.
	async _loadValidDefectIDs(pIDElementType)
	{
		if (!pIDElementType) { return []; }
		try { const joins = await this._getAllPages(`PhysicalAssetElementTypePhysicalAssetDefectJoins/FilteredTo/FBV~IDPhysicalAssetElementType~EQ~${pIDElementType}~FBV~Deleted~EQ~0`); return [ ...new Set(joins.map((j) => j.IDPhysicalAssetDefect).filter(Boolean)) ]; }
		catch (e) { return []; }
	}

	/**
	 * Header action: schedule an inspection of this asset. Hands the asset to the bespoke create screen, which
	 * resolves the project, previews the document packet, and creates all of it in one call.
	 */
	scheduleInspection()
	{
		if ((typeof window !== 'undefined') && window.location && this.idAsset)
		{
			window.location.hash = `#/PSRS/Inspection/Create/ForAsset/${ this.idAsset }`;
		}
	}

	async _resolveProjectID()
	{
		const tmpPJ = await this._getAllPages(`PhysicalAssetPolyJoins/FilteredTo/FBV~IDPhysicalAsset~EQ~${this.idAsset}~FBV~RemoteType~EQ~Project`);
		return tmpPJ.length ? tmpPJ[0].IDRemote : null;
	}

	async _loadObservations()
	{
		const tmpIDProject = await this._resolveProjectID();
		if (!tmpIDProject) { return { rows: [], idProject: null }; }
		const tmpRows = await this._get(`Observations/FilteredTo/FBV~IDProject~EQ~${tmpIDProject}~FBV~Deleted~EQ~0~FSF~CreateDate~DESC~0/0/50`);
		return { rows: Array.isArray(tmpRows) ? tmpRows.map((o) => ({ Type: o.ObservationType || o.Type || '', Date: o.CreateDate, Note: o.Note || o.Description || o.Name || '' })) : [], idProject: tmpIDProject };
	}

	async _loadDocuments()
	{
		const tmpJoins = await this._getAllPages(`PhysicalAssetArtifactJoins/FilteredTo/FBV~IDPhysicalAsset~EQ~${this.idAsset}`);
		const tmpIDs = [ ...new Set(tmpJoins.map((j) => j.IDArtifact).filter(Boolean)) ];
		if (!tmpIDs.length) { return []; }
		const tmpArtifacts = await this._getAllPages(`Artifacts/FilteredTo/FBL~IDArtifact~INN~${tmpIDs.join(',')}~FBV~Deleted~EQ~0`);
		return tmpArtifacts.map((a) => ({ Name: a.Name || a.FileName || `#${a.IDArtifact}`, Type: a.Type || a.ArtifactType || '', Date: a.CreateDate }));
	}

	// --- Map contract (light-update, never a full body render → live map is preserved) ---
	setCenter(pLat, pLng, pSyncMap)
	{
		this._center = { Latitude: Number(pLat), Longitude: Number(pLng) };
		this._centerDirty = true; this._locError = '';
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA && tmpCA.getElement('#AW-LocReadout').length > 0) { tmpCA.assignContent('#AW-LocReadout', this._locReadoutHTML()); }
		if (pSyncMap) { const m = this.pict.views.AssetLocationMap; if (m && typeof m.moveMarkerTo === 'function') { m.moveMarkerTo(this._center.Latitude, this._center.Longitude); } }
	}
	setCenterFromText(pLat, pLng)
	{
		const tmpLat = Number(pLat); const tmpLng = Number(pLng);
		if (Number.isNaN(tmpLat) || Number.isNaN(tmpLng) || tmpLat < -90 || tmpLat > 90 || tmpLng < -180 || tmpLng > 180)
		{ this._locError = 'Enter a valid latitude (−90..90) and longitude (−180..180).'; const tmpCA = this.pict.ContentAssignment; if (tmpCA && tmpCA.getElement('#AW-LocReadout').length > 0) { tmpCA.assignContent('#AW-LocReadout', this._locReadoutHTML()); } return; }
		this.setCenter(tmpLat, tmpLng, true);
	}
	async saveLocationAsync()
	{
		if (!this.canEdit || !this._asset) { return; }
		this._locError = ''; this._locSaving = true;
		const tmpCA = this.pict.ContentAssignment; if (tmpCA && tmpCA.getElement('#AW-LocReadout').length > 0) { tmpCA.assignContent('#AW-LocReadout', this._locReadoutHTML()); }
		try
		{
			await this._put('PhysicalAsset', { IDPhysicalAsset: this.idAsset, OriginLatitude: this._center.Latitude, OriginLongitude: this._center.Longitude });
			this._asset.OriginLatitude = this._center.Latitude; this._asset.OriginLongitude = this._center.Longitude;
			this._centerDirty = false;
		}
		catch (e) { this._locError = (e && e.message) || String(e); }
		finally { this._locSaving = false; if (tmpCA && tmpCA.getElement('#AW-LocReadout').length > 0) { tmpCA.assignContent('#AW-LocReadout', this._locReadoutHTML()); } }
	}

	// --- Tab bodies (all `render` kind; the base assigns the returned string into the tab body) ---
	_field(pLabel, pValue) { return `<div class="aw-field"><div class="aw-field-label">${this._esc(pLabel)}</div><div class="aw-field-value">${pValue == null || pValue === '' ? '<span class="aw-muted">—</span>' : this._esc(pValue)}</div></div>`; }

	_overviewHTML()
	{
		const a = this._asset || {};
		return `<div class="aw-fields">
			${this._field('Name', a.Name)}
			${this._field('Asset Number', a.AssetNumber)}
			${this._field('Structure (external)', a.ExternalSyncGUID)}
			${this._field('Asset Type', this._typeName)}
			${this._field('Classification', this._classificationName)}
			${this._field('Year Built', a.YearBuilt)}
			${this._field('Latitude', a.OriginLatitude)}
			${this._field('Longitude', a.OriginLongitude)}
		</div>
		${ this._customPropsHTML() }`;
	}

	_locReadoutHTML()
	{
		const c = this._center;
		const tmpCoords = this.hasCenter ? `${Number(c.Latitude).toFixed(6)}, ${Number(c.Longitude).toFixed(6)}` : 'Not set';
		const tmpSave = (this.canEdit && this._centerDirty) ? `<button class="aw-btn aw-btn-primary" ${this._locSaving ? 'disabled' : ''} onclick="_Pict.providers.AssetWorkspace.saveLocationAsync()">${this._locSaving ? 'Saving…' : 'Save location'}</button>` : '';
		const tmpErr = this._locError ? `<span class="aw-error-inline">${this._esc(this._locError)}</span>` : '';
		return `<div class="aw-loc-coords"><b>${tmpCoords}</b>${this._centerDirty ? ' <span class="aw-muted">(unsaved)</span>' : ''}</div>${tmpSave}${tmpErr}`;
	}

	_locationHTML()
	{
		const a = this._asset || {};
		const tmpBounds = (a.BoundingMinLatitude || a.BoundingMaxLatitude || a.BoundingMinLongitude || a.BoundingMaxLongitude)
			? `<div class="aw-field-label" style="margin-top:0.8rem">Bounding box</div><div class="aw-mono">${this._n(a.BoundingMinLatitude)}, ${this._n(a.BoundingMinLongitude)} → ${this._n(a.BoundingMaxLatitude)}, ${this._n(a.BoundingMaxLongitude)}</div>`
			: '';
		return `<div class="aw-loc">
			<div class="aw-loc-top"><div id="AW-LocReadout">${this._locReadoutHTML()}</div></div>
			<div id="AW-MapHost" class="aw-map"></div>
			${this.canEdit ? '<div class="aw-hint">Drag the marker to reposition the asset, then Save.</div>' : '<div class="aw-hint">Read-only — repositioning needs a super-user or customer-admin.</div>'}
			${tmpBounds}
		</div>`;
	}

	_csBar(pCS, pTotal)
	{
		if (!pCS) { return '<span class="aw-muted">—</span>'; }
		const parts = [ [ 'cs1', pCS.CS1 ], [ 'cs2', pCS.CS2 ], [ 'cs3', pCS.CS3 ], [ 'cs4', pCS.CS4 ] ];
		const total = Number(pTotal) || parts.reduce((a, [ , v ]) => a + (Number(v) || 0), 0) || 1;
		return `<span class="aw-csbar">${parts.map(([ cls, v ]) => { const w = Math.max(0, (Number(v) || 0) / total * 100); return w > 0 ? `<span class="aw-cs aw-${cls}" style="width:${w.toFixed(1)}%" title="${cls.toUpperCase()}: ${this._n(v)}"></span>` : ''; }).join('')}</span>`;
	}

	_tabState(pTab) { return this._tabs[pTab] || {}; }
	_loadingOrEmpty(pTab, pEmptyMsg)
	{
		const t = this._tabState(pTab);
		// Not yet loaded (and not errored) → show the spinner rather than a premature empty state (the base paints
		// the body once BEFORE the OnEnter load kicks in).
		if (t.Loading || (!t.Loaded && !t.Error)) { return `<div class="aw-note"><i class="fas fa-circle-notch fa-spin"></i> Loading…</div>`; }
		if (t.Error) { return `<div class="aw-error">${this._esc(t.Error)}</div>`; }
		if (!t.Rows || !t.Rows.length) { return `<div class="aw-note">${this._esc(pEmptyMsg)}</div>`; }
		return null;
	}

	// ========================================================= Elements (master → detail) =========================================================
	_elementsHTML()
	{
		if (this._elementDetail) { return this._elementDetailHTML(); }
		const t = this._tabState('elements');
		if (t.Loading || (!t.Loaded && !t.Error)) { return `<div class="aw-note"><i class="fas fa-circle-notch fa-spin"></i> Loading…</div>`; }
		if (t.Error) { return `<div class="aw-error">${this._esc(t.Error)}</div>`; }
		const rows = t.Rows || [];
		const err = this._elemError ? `<div class="aw-error">${this._esc(this._elemError)}</div>` : '';
		const add = this.canEdit
			? `<div class="aw-addbar"><span class="aw-addbar-l">Add an element</span><div id="AW-ElementTypePicker" class="aw-pick"></div></div>`
			: '';
		if (!rows.length)
		{
			return err + add + `<div class="aw-note">No elements on this asset yet${this.canEdit ? ' — add one above' : ''}, or use the <b>Migrate</b> tab to import legacy bridge element conditions.</div>`;
		}
		const fVal = (pCS, pKey) => (pCS && pCS[pKey] != null) ? this._n(pCS[pKey]) : '<span class="aw-muted">—</span>';
		const body = rows.map((e) =>
		{
			const c = e.Latest;
			const confirming = this._confirm && this._confirm.kind === 'element' && this._confirm.id === e.ID;
			const act = this.canEdit
				? (confirming
					? `<span class="aw-confirm-q">Remove?</span> <button class="aw-x aw-x-yes" title="Confirm remove" onclick="_Pict.providers.AssetWorkspace.confirmRemove()"><i class="fas fa-check"></i></button> <button class="aw-x" title="Cancel" onclick="_Pict.providers.AssetWorkspace.cancelRemove()"><i class="fas fa-xmark"></i></button>`
					: `<button class="aw-btn aw-btn-ghost aw-btn-sm" onclick="_Pict.providers.AssetWorkspace.openElement(${e.ID})"><i class="fas fa-sliders"></i> Manage</button> <button class="aw-x" title="Remove element" onclick="_Pict.providers.AssetWorkspace.askRemove('element',${e.ID})"><i class="fas fa-trash-can"></i></button>`)
				: `<button class="aw-btn aw-btn-ghost aw-btn-sm" onclick="_Pict.providers.AssetWorkspace.openElement(${e.ID})"><i class="fas fa-eye"></i> View</button>`;
			return `<tr>`
				+ `<td><a class="aw-link" onclick="_Pict.providers.AssetWorkspace.openElement(${e.ID})">${this._esc(e.Name)}</a>${e.Conditions.length ? ` <span class="aw-chip" title="Condition years on record">${e.Conditions.length} yr</span>` : ''}</td>`
				+ `<td class="aw-num">${this._n(e.Quantity)} ${this._esc(e.Units || '')}</td>`
				+ `<td>${c && c.Year ? this._esc(c.Year) : '<span class="aw-muted">—</span>'}</td>`
				+ `<td class="aw-num aw-cs1-t">${fVal(c && c.CS, 'CS1')}</td>`
				+ `<td class="aw-num aw-cs2-t">${fVal(c && c.CS, 'CS2')}</td>`
				+ `<td class="aw-num aw-cs3-t">${fVal(c && c.CS, 'CS3')}</td>`
				+ `<td class="aw-num aw-cs4-t">${fVal(c && c.CS, 'CS4')}</td>`
				+ `<td class="aw-csbar-cell">${this._csBar(c && c.CS, c && c.Total)}</td>`
				+ `<td class="aw-row-act">${act}</td>`
				+ `</tr>`;
		}).join('');
		return err + add + `<table class="aw-table"><thead><tr>`
			+ `<th>Element</th><th class="aw-num">Quantity</th><th>Latest</th>`
			+ `<th class="aw-num aw-cs1-h" title="Good">CS1</th><th class="aw-num aw-cs2-h" title="Fair">CS2</th><th class="aw-num aw-cs3-h" title="Poor">CS3</th><th class="aw-num aw-cs4-h" title="Severe">CS4</th>`
			+ `<th>Condition</th><th></th>`
			+ `</tr></thead><tbody>${body}</tbody></table>`;
	}

	_elementDetailHTML()
	{
		const d = this._elementDetail;
		const err = this._elemError ? `<div class="aw-error">${this._esc(this._elemError)}</div>` : '';
		const head = `<div class="aw-detail-head">
			<button class="aw-btn aw-btn-ghost aw-btn-sm" onclick="_Pict.providers.AssetWorkspace.closeElement()"><i class="fas fa-arrow-left"></i> Elements</button>
			<div class="aw-detail-title">${this._esc(d.Name)}</div>
			${d.Busy ? '<span class="aw-muted"><i class="fas fa-circle-notch fa-spin"></i></span>' : ''}
		</div>`;
		// Element basics (quantity is inline-editable)
		const basics = this.canEdit
			? `<div class="aw-basics"><label class="aw-inl">Quantity <input id="AW-Elem-Quantity" class="aw-in aw-in-num" type="number" step="any" value="${d.Quantity == null ? '' : this._esc(d.Quantity)}"></label><span class="aw-units">${this._esc(d.Units || '')}</span><button class="aw-btn aw-btn-primary aw-btn-sm" onclick="_Pict.providers.AssetWorkspace.saveElementBasics()"><i class="fas fa-check"></i> Save</button></div>`
			: `<div class="aw-basics"><span class="aw-field-label">Quantity</span> <b>${this._n(d.Quantity)}</b> <span class="aw-units">${this._esc(d.Units || '')}</span></div>`;
		return err + head + basics + this._conditionsHTML() + this._elementDefectsHTML();
	}

	// A CS1–4 (+ Year) input row shared by the condition and defect editors.
	_csEditorHTML(pPrefix, pSeed)
	{
		const s = pSeed || {}; const cs = s.CS || {};
		const inp = (k) => `<input id="AW-${pPrefix}-${k}" class="aw-in aw-in-num" type="number" step="any" min="0" value="${cs[k] == null ? '' : this._esc(cs[k])}" placeholder="0">`;
		return `<span class="aw-csedit">`
			+ `<label class="aw-inl aw-inl-yr">Year <input id="AW-${pPrefix}-Year" class="aw-in aw-in-yr" type="text" maxlength="24" value="${this._esc(s.Year || '')}" placeholder="2025"></label>`
			+ `<label class="aw-inl">CS1 ${inp('CS1')}</label>`
			+ `<label class="aw-inl">CS2 ${inp('CS2')}</label>`
			+ `<label class="aw-inl">CS3 ${inp('CS3')}</label>`
			+ `<label class="aw-inl">CS4 ${inp('CS4')}</label>`
			+ `</span>`;
	}
	_readCS(pPrefix)
	{
		const cs = { CS1: this._num0(this._val(`#AW-${pPrefix}-CS1`)), CS2: this._num0(this._val(`#AW-${pPrefix}-CS2`)), CS3: this._num0(this._val(`#AW-${pPrefix}-CS3`)), CS4: this._num0(this._val(`#AW-${pPrefix}-CS4`)) };
		return { CS: cs, Total: cs.CS1 + cs.CS2 + cs.CS3 + cs.CS4, Year: String(this._val(`#AW-${pPrefix}-Year`) || '').trim() };
	}

	_conditionsHTML()
	{
		const d = this._elementDetail; const conds = d.Conditions || [];
		const fVal = (pCS, pKey) => (pCS && pCS[pKey] != null) ? this._n(pCS[pKey]) : '<span class="aw-muted">—</span>';
		const editing = this._condEdit;
		const rowFor = (c) =>
		{
			if (editing && editing.ID === c.ID)
			{
				return `<tr class="aw-editing"><td colspan="7">${this._csEditorHTML('Cond', c)}<span class="aw-editbar"><button class="aw-btn aw-btn-primary aw-btn-sm" onclick="_Pict.providers.AssetWorkspace.saveCondition()"><i class="fas fa-check"></i> Save</button><button class="aw-btn aw-btn-ghost aw-btn-sm" onclick="_Pict.providers.AssetWorkspace.cancelEdit()">Cancel</button></span></td></tr>`;
			}
			const confirming = this._confirm && this._confirm.kind === 'condition' && this._confirm.id === c.ID;
			const act = this.canEdit
				? (confirming
					? `<span class="aw-confirm-q">Remove?</span> <button class="aw-x aw-x-yes" onclick="_Pict.providers.AssetWorkspace.confirmRemove()"><i class="fas fa-check"></i></button> <button class="aw-x" onclick="_Pict.providers.AssetWorkspace.cancelRemove()"><i class="fas fa-xmark"></i></button>`
					: `<button class="aw-x" title="Edit" onclick="_Pict.providers.AssetWorkspace.editCondition(${c.ID})"><i class="fas fa-pen"></i></button> <button class="aw-x" title="Remove" onclick="_Pict.providers.AssetWorkspace.askRemove('condition',${c.ID})"><i class="fas fa-trash-can"></i></button>`)
				: '';
			return `<tr>`
				+ `<td>${c.Year ? this._esc(c.Year) : '<span class="aw-muted">—</span>'}</td>`
				+ `<td class="aw-num aw-cs1-t">${fVal(c.CS, 'CS1')}</td>`
				+ `<td class="aw-num aw-cs2-t">${fVal(c.CS, 'CS2')}</td>`
				+ `<td class="aw-num aw-cs3-t">${fVal(c.CS, 'CS3')}</td>`
				+ `<td class="aw-num aw-cs4-t">${fVal(c.CS, 'CS4')}</td>`
				+ `<td class="aw-num">${this._n(c.Total)}</td>`
				+ `<td class="aw-row-act">${act}</td>`
				+ `</tr>`;
		};
		const body = conds.map(rowFor).join('');
		const addNew = (editing && editing.ID === 0)
			? `<tr class="aw-editing"><td colspan="7">${this._csEditorHTML('Cond', null)}<span class="aw-editbar"><button class="aw-btn aw-btn-primary aw-btn-sm" onclick="_Pict.providers.AssetWorkspace.saveCondition()"><i class="fas fa-check"></i> Add</button><button class="aw-btn aw-btn-ghost aw-btn-sm" onclick="_Pict.providers.AssetWorkspace.cancelEdit()">Cancel</button></span></td></tr>`
			: '';
		const addBtn = (this.canEdit && !editing) ? `<button class="aw-btn aw-btn-ghost aw-btn-sm" onclick="_Pict.providers.AssetWorkspace.addCondition()"><i class="fas fa-plus"></i> Add condition year</button>` : '';
		const empty = (!conds.length && !addNew) ? `<tr><td colspan="7"><span class="aw-muted">No condition years recorded${this.canEdit ? ' — add one.' : '.'}</span></td></tr>` : '';
		return `<div class="aw-sec"><div class="aw-sec-h"><span><i class="fas fa-chart-column"></i> Condition states by year</span>${addBtn}</div>`
			+ `<table class="aw-table aw-subtable"><thead><tr><th>Year</th><th class="aw-num aw-cs1-h">CS1</th><th class="aw-num aw-cs2-h">CS2</th><th class="aw-num aw-cs3-h">CS3</th><th class="aw-num aw-cs4-h">CS4</th><th class="aw-num">Total</th><th></th></tr></thead>`
			+ `<tbody>${body}${addNew}${empty}</tbody></table></div>`;
	}

	_elementDefectsHTML()
	{
		const d = this._elementDetail;
		if (d.DefectsError)
		{
			return `<div class="aw-sec"><div class="aw-sec-h"><span><i class="fas fa-triangle-exclamation"></i> Defects</span></div><div class="aw-note">${this._esc(d.DefectsError)}</div></div>`;
		}
		const defs = d.Defects || [];
		const fVal = (pCS, pKey) => (pCS && pCS[pKey] != null) ? this._n(pCS[pKey]) : '<span class="aw-muted">—</span>';
		const editing = this._defEdit;
		const rowFor = (x) =>
		{
			if (editing && editing.ID === x.ID)
			{
				return `<tr class="aw-editing"><td colspan="8"><div class="aw-defedit-name">${this._esc(x.Code ? x.Code + ' · ' : '')}${this._esc(x.Name)}</div>${this._csEditorHTML('Def', x)}<span class="aw-editbar"><button class="aw-btn aw-btn-primary aw-btn-sm" onclick="_Pict.providers.AssetWorkspace.saveDefect()"><i class="fas fa-check"></i> Save</button><button class="aw-btn aw-btn-ghost aw-btn-sm" onclick="_Pict.providers.AssetWorkspace.cancelEdit()">Cancel</button></span></td></tr>`;
			}
			const confirming = this._confirm && this._confirm.kind === 'defect' && this._confirm.id === x.ID;
			const act = this.canEdit
				? (confirming
					? `<span class="aw-confirm-q">Remove?</span> <button class="aw-x aw-x-yes" onclick="_Pict.providers.AssetWorkspace.confirmRemove()"><i class="fas fa-check"></i></button> <button class="aw-x" onclick="_Pict.providers.AssetWorkspace.cancelRemove()"><i class="fas fa-xmark"></i></button>`
					: `<button class="aw-x" title="Edit" onclick="_Pict.providers.AssetWorkspace.editDefect(${x.ID})"><i class="fas fa-pen"></i></button> <button class="aw-x" title="Remove" onclick="_Pict.providers.AssetWorkspace.askRemove('defect',${x.ID})"><i class="fas fa-trash-can"></i></button>`)
				: '';
			return `<tr>`
				+ `<td class="aw-mono">${this._esc(x.Code || '')}</td>`
				+ `<td>${this._esc(x.Name)}</td>`
				+ `<td>${x.Year ? this._esc(x.Year) : '<span class="aw-muted">—</span>'}</td>`
				+ `<td class="aw-num aw-cs1-t">${fVal(x.CS, 'CS1')}</td>`
				+ `<td class="aw-num aw-cs2-t">${fVal(x.CS, 'CS2')}</td>`
				+ `<td class="aw-num aw-cs3-t">${fVal(x.CS, 'CS3')}</td>`
				+ `<td class="aw-num aw-cs4-t">${fVal(x.CS, 'CS4')}</td>`
				+ `<td class="aw-row-act">${act}</td>`
				+ `</tr>`;
		};
		const body = defs.map(rowFor).join('');
		const addNew = (editing && editing.ID === 0)
			? `<tr class="aw-editing"><td colspan="8"><div class="aw-defadd"><span class="aw-addbar-l">Defect</span><div id="AW-DefectPicker" class="aw-pick"></div></div>${this._csEditorHTML('Def', null)}<span class="aw-editbar"><button class="aw-btn aw-btn-primary aw-btn-sm" onclick="_Pict.providers.AssetWorkspace.saveDefect()"><i class="fas fa-check"></i> Add</button><button class="aw-btn aw-btn-ghost aw-btn-sm" onclick="_Pict.providers.AssetWorkspace.cancelEdit()">Cancel</button></span></td></tr>`
			: '';
		const addBtn = (this.canEdit && !editing) ? `<button class="aw-btn aw-btn-ghost aw-btn-sm" onclick="_Pict.providers.AssetWorkspace.addDefect()"><i class="fas fa-plus"></i> Add defect</button>` : '';
		const empty = (!defs.length && !addNew) ? `<tr><td colspan="8"><span class="aw-muted">No defects recorded against this element${this.canEdit ? ' — add one.' : '.'}</span></td></tr>` : '';
		return `<div class="aw-sec"><div class="aw-sec-h"><span><i class="fas fa-triangle-exclamation"></i> Defects on this element</span>${addBtn}</div>`
			+ `<table class="aw-table aw-subtable"><thead><tr><th>Code</th><th>Defect</th><th>Year</th><th class="aw-num aw-cs1-h">CS1</th><th class="aw-num aw-cs2-h">CS2</th><th class="aw-num aw-cs3-h">CS3</th><th class="aw-num aw-cs4-h">CS4</th><th></th></tr></thead>`
			+ `<tbody>${body}${addNew}${empty}</tbody></table></div>`;
	}

	// --- Element interactions ---
	_repaintElements() { this.repaintBody(); this._scheduleElementsMount(); }

	async openElement(pID)
	{
		const id = Number(pID) || 0; if (!id) { return; }
		this._elemError = ''; this._condEdit = null; this._defEdit = null; this._confirm = null;
		const row = (this._tabState('elements').Rows || []).find((r) => r.ID === id);
		if (!row) { this._reload('elements'); return; }
		this._elementDetail = Object.assign({}, row, { Busy: true, Defects: [], DefectsError: '', ValidDefectIDs: [] });
		this._repaintElements();
		try
		{
			this._elementDetail.ValidDefectIDs = await this._loadValidDefectIDs(row.IDType);
			this._elementDetail.Defects = await this._loadElementDefects(id);
		}
		catch (e) { this._elementDetail.DefectsError = `Defects could not be loaded — ${((e && e.message) || String(e))}. (The element↔defect join columns may not be deployed yet.)`; }
		finally { if (this._elementDetail) { this._elementDetail.Busy = false; } this._repaintElements(); }
	}
	closeElement() { this._elementDetail = null; this._condEdit = null; this._defEdit = null; this._confirm = null; this._elemError = ''; this._repaintElements(); }
	_reload(pTab) { if (this._tabs[pTab]) { this._tabs[pTab].Loaded = false; } this._ensureTabDataAsync(pTab, true); }

	// Re-fetch the open element's conditions + defects without the full-list spinner flash.
	async _refreshDetail()
	{
		const id = this._elementDetail && this._elementDetail.ID; if (!id) { return; }
		this._elementDetail.Busy = true; this._repaintElements();
		try
		{
			const rows = await this._loadElements();
			this._tabs.elements = { Loaded: true, Rows: rows };
			const row = rows.find((r) => r.ID === id);
			const defects = await this._loadElementDefects(id).catch((e) => { this._elementDetail.DefectsError = `Defects could not be loaded — ${((e && e.message) || String(e))}.`; return this._elementDetail.Defects || []; });
			if (row) { this._elementDetail = Object.assign({}, row, { Busy: false, Defects: defects, DefectsError: this._elementDetail.DefectsError || '', ValidDefectIDs: this._elementDetail.ValidDefectIDs || [] }); }
			else { this._elementDetail = null; }
		}
		catch (e) { this._elemError = (e && e.message) || String(e); if (this._elementDetail) { this._elementDetail.Busy = false; } }
		finally { this._repaintElements(); }
	}

	async addElement(pIDType)
	{
		const id = Number(pIDType) || 0; if (!id || !this.canEdit) { return; }
		this._elemError = '';
		try
		{
			const et = await this._get(`PhysicalAssetElementType/${id}`);
			const payload = {
				Name: (et && (et.Name || et.Code)) || `Element ${id}`,
				Units: (et && et.Unit) || '',
				Quantity: 0,
				IDPhysicalAsset: this.idAsset,
				IDAssetType: (this._asset && this._asset.IDAssetType) || 0,
				IDPhysicalAssetElementType: id,
				IDPhysicalAssetMaterial: (et && et.IDPhysicalAssetMaterial) || 0,
			};
			const r = await this._post('PhysicalAssetElements', payload);
			if (r && r.Error) { throw new Error(r.Error); }
		}
		catch (e) { this._elemError = `Add element failed — ${(e && e.message) || String(e)}`; }
		if (this.pict && this.pict.AppData && this.pict.AppData.AssetWorkspace) { this.pict.AppData.AssetWorkspace.ElementTypePicker = null; }
		this._reload('elements');
	}

	async saveElementBasics()
	{
		const d = this._elementDetail; if (!d || !this.canEdit) { return; }
		this._elemError = '';
		const qty = this._num0(this._val('#AW-Elem-Quantity'));
		try { const r = await this._put('PhysicalAssetElement', { IDPhysicalAssetElement: d.ID, Quantity: qty }); if (r && r.Error) { throw new Error(r.Error); } d.Quantity = qty; }
		catch (e) { this._elemError = `Save failed — ${(e && e.message) || String(e)}`; }
		this._repaintElements();
	}

	// --- condition editing ---
	addCondition() { if (!this.canEdit) { return; } this._condEdit = { ID: 0 }; this._confirm = null; this._repaintElements(); }
	editCondition(pID) { if (!this.canEdit) { return; } this._condEdit = { ID: Number(pID) || 0 }; this._confirm = null; this._repaintElements(); }
	cancelEdit() { this._condEdit = null; this._defEdit = null; this._repaintElements(); }
	async saveCondition()
	{
		const d = this._elementDetail; if (!d || !this.canEdit || !this._condEdit) { return; }
		this._elemError = '';
		const r = this._readCS('Cond');
		const payload = {
			IDPhysicalAssetElement: d.ID, IDPhysicalAsset: this.idAsset,
			Year: r.Year, TotalQuantity: r.Total, QuantitiesJSON: JSON.stringify(r.CS),
			Source: 'Manual', EffectiveDate: /^\d{4}$/.test(r.Year) ? `${r.Year}-01-01T00:00:00Z` : null,
		};
		try
		{
			if (this._condEdit.ID) { payload.IDPhysicalAssetElementCondition = this._condEdit.ID; const x = await this._put('PhysicalAssetElementCondition', payload); if (x && x.Error) { throw new Error(x.Error); } }
			else { const x = await this._post('PhysicalAssetElementConditions', payload); if (x && x.Error) { throw new Error(x.Error); } }
			this._condEdit = null;
		}
		catch (e) { this._elemError = `Save condition failed — ${(e && e.message) || String(e)}`; }
		await this._refreshDetail();
	}

	// --- defect editing ---
	addDefect() { if (!this.canEdit) { return; } this._defEdit = { ID: 0, IDDefect: 0 }; this._confirm = null; if (this.pict && this.pict.AppData && this.pict.AppData.AssetWorkspace) { this.pict.AppData.AssetWorkspace.DefectPicker = null; } this._repaintElements(); }
	editDefect(pID)
	{
		if (!this.canEdit) { return; }
		const x = (this._elementDetail.Defects || []).find((y) => y.ID === (Number(pID) || 0));
		this._defEdit = x ? { ID: x.ID, IDDefect: x.IDDefect, Code: x.Code, Name: x.Name } : null;
		this._confirm = null; this._repaintElements();
	}
	async saveDefect()
	{
		const d = this._elementDetail; if (!d || !this.canEdit || !this._defEdit) { return; }
		this._elemError = '';
		const r = this._readCS('Def');
		let idDefect = this._defEdit.IDDefect;
		if (!this._defEdit.ID) { idDefect = Number(this._defEdit.PickedID) || Number((this.pict.AppData.AssetWorkspace || {}).DefectPicker) || 0; if (!idDefect) { this._elemError = 'Pick a defect to add.'; this._repaintElements(); return; } }
		const payload = {
			IDPhysicalAssetElement: d.ID, IDPhysicalAssetDefect: idDefect,
			Year: r.Year, TotalQuantity: r.Total, QuantitiesJSON: JSON.stringify(r.CS),
		};
		try
		{
			if (this._defEdit.ID) { payload.IDPhysicalAssetElementPhysicalAssetDefectJoin = this._defEdit.ID; const x = await this._put('PhysicalAssetElementPhysicalAssetDefectJoin', payload); if (x && x.Error) { throw new Error(x.Error); } }
			else { const x = await this._post('PhysicalAssetElementPhysicalAssetDefectJoins', payload); if (x && x.Error) { throw new Error(x.Error); } }
			this._defEdit = null;
		}
		catch (e) { this._elemError = `Save defect failed — ${(e && e.message) || String(e)}. (The element↔defect join columns may not be deployed yet.)`; }
		await this._refreshDetail();
	}

	// --- shared inline delete confirm ---
	askRemove(pKind, pID) { if (!this.canEdit) { return; } this._confirm = { kind: pKind, id: Number(pID) || 0 }; this._condEdit = null; this._defEdit = null; this._repaintElements(); }
	cancelRemove() { this._confirm = null; this._repaintElements(); }
	async confirmRemove()
	{
		const c = this._confirm; if (!c || !this.canEdit) { return; }
		this._confirm = null; this._elemError = '';
		try
		{
			if (c.kind === 'element') { const x = await this._del(`PhysicalAssetElement/${c.id}`); if (x && x.Error) { throw new Error(x.Error); } this._elementDetail = null; this._reload('elements'); return; }
			if (c.kind === 'condition') { const x = await this._del(`PhysicalAssetElementCondition/${c.id}`); if (x && x.Error) { throw new Error(x.Error); } await this._refreshDetail(); return; }
			if (c.kind === 'defect') { const x = await this._del(`PhysicalAssetElementPhysicalAssetDefectJoin/${c.id}`); if (x && x.Error) { throw new Error(x.Error); } await this._refreshDetail(); return; }
		}
		catch (e) { this._elemError = `Remove failed — ${(e && e.message) || String(e)}`; this._repaintElements(); }
	}

	// --- pickers (element-type on the list add-bar, defect inside the element detail add form) ---
	_scheduleElementsMount() { try { setTimeout(() => this._mountElementsPickers(), 0); } catch (e) { /* */ } }
	_picker() { return this.pict.providers && this.pict.providers['Pict-Section-Picker']; }
	_mountElementsPickers()
	{
		const tmpPicker = this._picker(); const tmpCA = this.pict.ContentAssignment;
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function') || !tmpCA) { return; }
		const mount = (pHostID, pName, pDataProvider, pOnChange, pPlaceholder) =>
		{
			if (tmpCA.getElement(`#${pHostID}`).length === 0) { return; }
			const v = tmpPicker.createPicker(`${this.providerName}-${pName}`, {
				DestinationAddress: `#${pHostID}`, ValueAddress: `AppData.AssetWorkspace.${pName}`,
				Searchable: true, AllowClear: true, ClearLabel: `— ${pPlaceholder} —`, Placeholder: `${pPlaceholder}…`,
				DataProvider: pDataProvider, ResolveValue: () => Promise.resolve(null), OnChange: pOnChange,
			});
			if (v && (typeof v.render === 'function')) { v.render(); }
		};
		if (this._elementDetail && this._defEdit && this._defEdit.ID === 0)
		{
			mount('AW-DefectPicker', 'DefectPicker', this._defectDataProvider(), (val) => { if (this._defEdit) { this._defEdit.PickedID = Number(val) || 0; } }, 'search defects');
		}
		else if (!this._elementDetail && this.canEdit)
		{
			mount('AW-ElementTypePicker', 'ElementTypePicker', this._elementTypeDataProvider(), (val) => { if (val) { this.addElement(val); } }, 'search element types');
		}
	}
	_elementTypeDataProvider()
	{
		return libEntityPickerSearch.entityPickerDataProvider((pURL) => this._get(pURL),
			{
				Set: 'PhysicalAssetElementTypes', IDField: 'IDPhysicalAssetElementType', SortField: 'Code',
				ToOption: (r) => ({ Value: Number(r.IDPhysicalAssetElementType), Text: (r.Code ? `${r.Code} · ` : '') + (r.Name || `Type ${r.IDPhysicalAssetElementType}`), Record: r }),
			});
	}
	_defectDataProvider()
	{
		return libEntityPickerSearch.entityPickerDataProvider((pURL) => this._get(pURL),
			{
				Set: 'PhysicalAssetDefects', IDField: 'IDPhysicalAssetDefect', SortField: 'Code',
				Scope: () =>
				{
					const ids = (this._elementDetail && this._elementDetail.ValidDefectIDs) || [];
					return ids.length ? `FBL~IDPhysicalAssetDefect~INN~${ids.join(',')}~FBV~Deleted~EQ~0` : 'FBV~Deleted~EQ~0';
				},
				ToOption: (r) => ({ Value: Number(r.IDPhysicalAssetDefect), Text: (r.Code ? `${r.Code} · ` : '') + (r.Name || `Defect ${r.IDPhysicalAssetDefect}`), Record: r }),
			});
	}

	_defectsHTML()
	{
		const tmpBusy = this._loadingOrEmpty('defects', 'No defects recorded against this asset.');
		if (tmpBusy) { return tmpBusy; }
		const rows = this._tabState('defects').Rows.map((d) => `<tr><td class="aw-mono">${this._esc(d.Code)}</td><td>${this._esc(d.Name)}</td><td>${this._esc(d.Description || '')}</td></tr>`).join('');
		return `<div class="aw-hint">Asset-level defect roll-up. Defects recorded against a specific element — with their condition-state breakdown — live in that element's <b>Manage</b> view on the Elements tab.</div><table class="aw-table"><thead><tr><th>Code</th><th>Name</th><th>Description</th></tr></thead><tbody>${rows}</tbody></table>`;
	}

	_observationsHTML()
	{
		const tmpBusy = this._loadingOrEmpty('observations', 'No observations found for this asset’s project (or the asset isn’t linked to a project yet).');
		if (tmpBusy) { return tmpBusy; }
		const rows = this._tabState('observations').Rows.map((o) => `<tr><td>${this._esc(o.Type)}</td><td>${o.Date ? this._esc(String(o.Date).slice(0, 10)) : ''}</td><td>${this._esc(String(o.Note || '').slice(0, 120))}</td></tr>`).join('');
		return `<div class="aw-hint">Observations from this asset’s linked project (newest first).</div><table class="aw-table"><thead><tr><th>Type</th><th>Date</th><th>Note</th></tr></thead><tbody>${rows}</tbody></table>`;
	}

	_documentsHTML()
	{
		const tmpBusy = this._loadingOrEmpty('documents', 'No documents linked to this asset yet.');
		if (tmpBusy) { return tmpBusy; }
		const rows = this._tabState('documents').Rows.map((d) => `<tr><td>${this._esc(d.Name)}</td><td>${this._esc(d.Type || '')}</td><td>${d.Date ? this._esc(String(d.Date).slice(0, 10)) : ''}</td></tr>`).join('');
		return `<table class="aw-table"><thead><tr><th>Name</th><th>Type</th><th>Added</th></tr></thead><tbody>${rows}</tbody></table>`;
	}

	// --- Migrate (single asset; mirrors the CLI + AssetBrowser) ---
	askMigrate() { this._migrate.Confirming = true; this.repaintBody(); }
	cancelMigrate() { this._migrate.Confirming = false; this.repaintBody(); }
	async loadMigratePlanAsync() { await this._runMigration(true); }
	async migrateAsync() { await this._runMigration(false); }

	async _runMigration(pDryRun)
	{
		const m = this._migrate;
		if (m.Applying || !this._asset) { return; }
		if (!pDryRun && !this.canEdit) { m.Error = 'Migrating needs a super-user or customer-admin.'; this.repaintBody(); return; }
		m.Applying = true; m.Error = ''; m.Confirming = false; if (!pDryRun) { m.Result = null; } this.repaintBody();
		const tmpAssetID = this.idAsset;
		try
		{
			const tmpETypes = await this._getAllPages('PhysicalAssetElementTypes/FilteredTo/FBV~Deleted~EQ~0');
			if (!tmpETypes.length) { throw new Error('No element-type catalog for this customer — run the AASHTO catalog Sync first (#/AssetCatalogSync).'); }
			const tmpETByCode = AssetWorkspaceProvider._index(tmpETypes, 'Code');
			const tmpLakeElemById = AssetWorkspaceProvider._index(await this._getAllPages('Elements'), 'IDElement');
			const tmpPJ = await this._getAllPages(`PhysicalAssetPolyJoins/FilteredTo/FBV~IDPhysicalAsset~EQ~${tmpAssetID}~FBV~RemoteType~EQ~Bridge`);
			const tmpIDBridge = tmpPJ.length ? tmpPJ[0].IDRemote : null;
			if (!tmpIDBridge) { throw new Error('This asset is not linked to a bridge (no element source).'); }
			const tmpConditions = await this._getAllPages(`BridgeElementConditions/FilteredTo/FBV~IDBridge~EQ~${tmpIDBridge}`);

			const tmpByElement = {}; tmpConditions.forEach((r) => { const k = String(r.IDElement); (tmpByElement[k] = tmpByElement[k] || []).push(r); });
			const tmpExisting = await this._getAllPages(`PhysicalAssetElements/FilteredTo/FBV~IDPhysicalAsset~EQ~${tmpAssetID}~FBV~Deleted~EQ~0`);
			const tmpElemByType = AssetWorkspaceProvider._index(tmpExisting, 'IDPhysicalAssetElementType');
			const tmpExistingCond = await this._getAllPages(`PhysicalAssetElementConditions/FilteredTo/FBV~IDPhysicalAsset~EQ~${tmpAssetID}~FBV~Deleted~EQ~0`);
			const tmpCondSig = {}; tmpExistingCond.forEach((c) => { tmpCondSig[`${c.IDPhysicalAssetElement}|${c.Year}`] = true; });

			const tmpElementsToCreate = []; const tmpTypeByLakeElem = {}; let tmpUnknown = 0;
			Object.keys(tmpByElement).forEach((k) =>
			{
				const le = tmpLakeElemById[k]; const et = le ? tmpETByCode[String(le.Code)] : null;
				if (!et) { tmpUnknown++; return; }
				tmpTypeByLakeElem[k] = et;
				let latest = null; tmpByElement[k].forEach((r) => { if (!latest || String(r.Year) > String(latest.Year)) { latest = r; } });
				if (tmpElemByType[String(et.IDPhysicalAssetElementType)]) { return; }
				tmpElementsToCreate.push({ Name: et.Name || `Element ${et.Code}`, Units: et.Unit || '', Quantity: latest ? latest.TotalQuantity : 0, IDPhysicalAsset: tmpAssetID, IDAssetType: this._asset.IDAssetType || 0, IDPhysicalAssetElementType: et.IDPhysicalAssetElementType, IDPhysicalAssetMaterial: et.IDPhysicalAssetMaterial || 0 });
			});
			let tmpCondPlan = 0;
			Object.keys(tmpByElement).forEach((k) => { const et = tmpTypeByLakeElem[k]; if (!et) { return; } const ex = tmpElemByType[String(et.IDPhysicalAssetElementType)]; tmpByElement[k].forEach((r) => { if (ex && tmpCondSig[`${ex.IDPhysicalAssetElement}|${r.Year}`]) { return; } tmpCondPlan++; }); });

			if (pDryRun) { m.Plan = { bridge: tmpIDBridge, elementsToCreate: tmpElementsToCreate.length, conditionsToCreate: tmpCondPlan, unknown: tmpUnknown }; return; }

			let tmpElemCreated = 0;
			if (tmpElementsToCreate.length) { const r = await this._post('PhysicalAssetElements', tmpElementsToCreate); if (r && r.Error) { throw new Error(r.Error); } tmpElemCreated = tmpElementsToCreate.length; }
			const tmpRefreshed = await this._getAllPages(`PhysicalAssetElements/FilteredTo/FBV~IDPhysicalAsset~EQ~${tmpAssetID}~FBV~Deleted~EQ~0`);
			const tmpElemByTypeNow = AssetWorkspaceProvider._index(tmpRefreshed, 'IDPhysicalAssetElementType');
			const tmpCondsToCreate = [];
			Object.keys(tmpByElement).forEach((k) =>
			{
				const et = tmpTypeByLakeElem[k]; if (!et) { return; } const elemRow = tmpElemByTypeNow[String(et.IDPhysicalAssetElementType)]; if (!elemRow) { return; }
				tmpByElement[k].forEach((r) => { const sig = `${elemRow.IDPhysicalAssetElement}|${r.Year}`; if (tmpCondSig[sig]) { return; } tmpCondSig[sig] = true; const y = String(r.Year || ''); tmpCondsToCreate.push({ IDPhysicalAssetElement: elemRow.IDPhysicalAssetElement, IDPhysicalAsset: tmpAssetID, Year: y, EffectiveDate: /^\d{4}$/.test(y) ? `${y}-01-01T00:00:00Z` : null, Source: r.Source || '', TotalQuantity: r.TotalQuantity, QuantitiesJSON: JSON.stringify({ CS1: r.CS1, CS2: r.CS2, CS3: r.CS3, CS4: r.CS4 }) }); });
			});
			let tmpCondCreated = 0;
			if (tmpCondsToCreate.length) { const r = await this._post('PhysicalAssetElementConditions', tmpCondsToCreate); if (r && r.Error) { throw new Error(r.Error); } tmpCondCreated = tmpCondsToCreate.length; }
			m.Result = { elementsCreated: tmpElemCreated, conditionsCreated: tmpCondCreated, unknown: tmpUnknown }; m.Plan = null;
			this._tabs.elements = { Loaded: false }; // force refresh of the Elements tab
		}
		catch (e) { m.Error = (e && e.message) || String(e); }
		finally { m.Applying = false; this.repaintBody(); }
	}

	_migrateHTML()
	{
		const m = this._migrate;
		let h = `<div class="aw-hint">Bring this asset’s legacy bridge element-condition data into the modern model. Additive &amp; safe to re-run. (For bulk, use the migration CLI.)</div>`;
		if (m.Applying) { return h + `<div class="aw-note"><i class="fas fa-circle-notch fa-spin"></i> Working…</div>`; }
		if (m.Error) { h += `<div class="aw-error">${this._esc(m.Error)}</div>`; }
		if (m.Result) { h += `<div class="aw-result"><i class="fas fa-circle-check"></i> Migrated — ${this._n(m.Result.elementsCreated)} elements, ${this._n(m.Result.conditionsCreated)} conditions${m.Result.unknown ? ` (${this._n(m.Result.unknown)} unknown skipped)` : ''}.</div>`; }
		if (m.Plan)
		{
			h += `<div class="aw-plan">Plan: <b>${this._n(m.Plan.elementsToCreate)}</b> elements + <b>${this._n(m.Plan.conditionsToCreate)}</b> conditions from lake bridge ${this._n(m.Plan.bridge)}${m.Plan.unknown ? ` · ${this._n(m.Plan.unknown)} unknown skipped` : ''}.</div>`;
			const tmpWork = (m.Plan.elementsToCreate + m.Plan.conditionsToCreate) > 0;
			if (this.canEdit && tmpWork)
			{
				h += m.Confirming
					? `<div class="aw-actions"><span class="aw-confirm-q">Migrate this asset?</span><button class="aw-btn aw-btn-primary" onclick="_Pict.providers.AssetWorkspace.migrateAsync()">Yes, migrate</button><button class="aw-btn aw-btn-ghost" onclick="_Pict.providers.AssetWorkspace.cancelMigrate()">Cancel</button></div>`
					: `<div class="aw-actions"><button class="aw-btn aw-btn-primary" onclick="_Pict.providers.AssetWorkspace.askMigrate()"><i class="fas fa-cloud-arrow-down"></i> Migrate this asset</button></div>`;
			}
			else if (!tmpWork) { h += `<div class="aw-note">Nothing to migrate — already up to date.</div>`; }
			else { h += `<div class="aw-note">Migrating needs a super-user or customer-admin.</div>`; }
		}
		else { h += `<div class="aw-actions"><button class="aw-btn aw-btn-ghost" onclick="_Pict.providers.AssetWorkspace.loadMigratePlanAsync()"><i class="fas fa-list-check"></i> Check migration (dry run)</button></div>`; }
		return h;
	}
}

module.exports = AssetWorkspaceProvider;
